import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Shield, CheckCircle2, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeadModal({ isOpen, onClose }) {
  const [form, setForm] = useState({ name: '', email: '', website: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      setError('Name and email are required');
      return;
    }
    setError('');
    setSubmitting(true);

    try {
      await axios.post(`${API}/leads`, form);
      setSubmitted(true);
    } catch (err) {
      setError('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setForm({ name: '', email: '', website: '', message: '' });
    setSubmitted(false);
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          className="w-full max-w-md bg-[#0a0a0a] border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#00ff9d]" />
              <h3 className="text-lg font-semibold text-white">Protect Your Website</h3>
            </div>
            <button onClick={handleClose} className="p-1 text-white/40 hover:text-white transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {submitted ? (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center py-8"
                data-testid="lead-form-success-message"
              >
                <CheckCircle2 className="w-16 h-16 text-[#00ff9d] mx-auto mb-4" />
                <h4 className="text-xl font-semibold text-[#00ff9d] mb-2">Thank You!</h4>
                <p className="text-sm text-white/60">
                  Our team will contact you within 24 hours to discuss your protection needs.
                </p>
                <button
                  onClick={handleClose}
                  className="mt-6 px-6 py-2 bg-white/5 border border-white/10 rounded-lg text-white/70 text-sm hover:bg-white/10 transition-colors"
                >
                  Close
                </button>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <p className="text-sm text-white/50 mb-4">
                  Fill in your details and our security team will reach out to protect your infrastructure.
                </p>

                <div>
                  <label className="block text-xs font-medium text-white/60 mb-1.5">Name *</label>
                  <input
                    data-testid="lead-form-name-input"
                    name="name"
                    type="text"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Your full name"
                    className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[#00ff9d]/50 transition-shadow"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-white/60 mb-1.5">Email *</label>
                  <input
                    data-testid="lead-form-email-input"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="you@company.com"
                    className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[#00ff9d]/50 transition-shadow"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-white/60 mb-1.5">Website URL</label>
                  <input
                    data-testid="lead-form-website-input"
                    name="website"
                    type="url"
                    value={form.website}
                    onChange={handleChange}
                    placeholder="https://yourwebsite.com"
                    className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[#00ff9d]/50 transition-shadow"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-white/60 mb-1.5">Message</label>
                  <textarea
                    data-testid="lead-form-message-textarea"
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Tell us about your protection needs..."
                    rows={3}
                    className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[#00ff9d]/50 transition-shadow resize-none"
                  />
                </div>

                {error && (
                  <p className="text-xs text-red-400">{error}</p>
                )}

                <button
                  data-testid="lead-form-submit-button"
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3 bg-[#00ff9d] hover:bg-[#00ff9d]/90 text-[#0a0a0a] font-semibold rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</>
                  ) : (
                    'Protect My Site'
                  )}
                </button>
              </form>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
